import React from 'react';

/**
 * Leading and trailing spaces in the fields that identify an account.
 *
 * WHY THIS IS NOT COSMETIC
 * `username` is the key for password sign-in. An account stored as "name " can only be reached
 * by typing a space nobody can see, and the person who typed it will not know they did. Ten
 * accounts were in that state before the 2026-09-14 repair, and a new one appeared the day after
 * it - typed into the Google sign-up form, where Google supplies the verified email and the
 * parent still chooses a username by hand.
 *
 * The server trims every creation path regardless (routes/auth.js, routes/users.js), so nothing
 * untrimmed can be stored. This exists for a different reason: to show the person what their
 * username is actually going to be, before they commit to signing in with it. Trimming silently
 * would store the right thing and still leave them believing the space was part of it.
 *
 * Deliberately only the edges. Spaces inside a name are legitimate ("Mary Beth") and are left
 * alone; so are all other characters, because this is a hygiene prompt and not a validator.
 *
 * WHY IT IS ALWAYS THE USERNAME AND NEVER THE EMAIL
 * An <input type="email"> runs the HTML value sanitization algorithm, which strips leading and
 * trailing whitespace before the value is ever readable - verified in the browser: setting
 * "  a@b.c  " reads back as "a@b.c". A username field is type="text" and has no such protection.
 * That is not a coincidence in the data, it is structural: the one untrimmed account found in
 * production had a trailing space on the username and a perfectly clean email.
 *
 * Email is still checked here, because the protection belongs to the input element rather than
 * to us: a form that renders the address as type="text", or a field that is later retyped, would
 * lose it silently. The check costs nothing when the browser has already done the work.
 */

/** True when the value has a leading or trailing space, tab or newline. */
export function hasEdgeWhitespace(value) {
  if (value == null) return false;
  const s = String(value);
  return s.length > 0 && s !== s.trim();
}

/** Which of the named fields have edge whitespace. `{ Username: 'bob ' }` -> `['Username']`. */
export function fieldsWithEdgeWhitespace(fields) {
  return Object.entries(fields || {})
    .filter(([, value]) => hasEdgeWhitespace(value))
    .map(([label]) => label);
}

/** Trim every value in the object, leaving anything that is not a string untouched. */
export function trimFields(fields) {
  const out = { ...fields };
  for (const [key, value] of Object.entries(out)) {
    if (typeof value === 'string') out[key] = value.trim();
  }
  return out;
}

/**
 * The prompt itself. Renders nothing when the fields are clean, so it can sit unconditionally in
 * a form. `fields` is keyed by the label the person sees, so the message names the field they
 * are looking at rather than an internal key.
 */
export function EdgeWhitespaceNotice({ fields, onFix }) {
  const affected = fieldsWithEdgeWhitespace(fields);
  if (affected.length === 0) return null;

  // Neutral phrasing on purpose: the same notice appears on the parent's own sign-up form and
  // on the district screen where an admin creates an account for a colleague. "Your username"
  // is wrong in the second case, and the person who would be locked out is not the person
  // reading the message.
  const which = affected.length === 1
    ? `The ${affected[0].toLowerCase()} has`
    : `The ${affected.map((a) => a.toLowerCase()).join(' and ')} have`;

  return (
    <div
      role="status"
      style={{
        margin: '0.75rem 0',
        padding: '0.75rem 1rem',
        border: '1px solid #ffc107',
        background: '#fff8e1',
        borderRadius: '6px',
        fontSize: '0.9em',
        color: '#5d4a00'
      }}
    >
      <strong>Check for extra spaces.</strong>{' '}
      {which} a space at the start or end. That space has to be typed at every sign-in, and it
      is invisible.
      {onFix && (
        <>
          {' '}
          <button
            type="button"
            onClick={onFix}
            style={{
              marginTop: '0.5rem',
              display: 'inline-block',
              background: '#ffc107',
              color: '#212529',
              border: 'none',
              borderRadius: '4px',
              padding: '0.35rem 0.75rem',
              cursor: 'pointer',
              fontWeight: 600
            }}
          >
            Remove the extra spaces
          </button>
        </>
      )}
    </div>
  );
}

const accountFieldHygiene = {
  hasEdgeWhitespace,
  fieldsWithEdgeWhitespace,
  trimFields,
  EdgeWhitespaceNotice
};
export default accountFieldHygiene;
